import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, Button, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { LeadAgenciesQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo, useState } from "react";

import { ConfirmDeleteNJLeadAgencies } from "./ConfirmDeleteNJLeadAgencies";
import { LeadAgencyDialog } from "./LeadAgencyDialog";
import { GET_ALL_NJ_LEAD_AGENECIES } from "./schema";

type Row = LeadAgenciesQuery["leadAgencies"]["items"][number];

type NJLeadAgenciesProps = {
  initialPageSize?: number;
};

export const NJLeadAgencies: React.FC<NJLeadAgenciesProps> = ({ initialPageSize }) => {
  const [openAgencyDialog, setOpenAgencyDialog] = useState<{
    mode: "add" | "edit";
    agency?: Row;
  } | null>(null);

  const [confirmDeleteLeadAgency, setConfirmDeleteLeadAgency] = useState<Row | null>(null);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      { field: "id", headerName: "Id", width: 50 },
      { field: "name", headerName: "Name", flex: 1 },
      { field: "address", headerName: "Address", flex: 1 },
      { field: "attn", headerName: "Attn", width: 180 },
      { field: "city", headerName: "City", flex: 1 },
      { field: "state", headerName: "State/Territory", width: 75 },
      { field: "zip", headerName: "Zip", width: 75 },
      { field: "consolidated", headerName: "Consolidated", flex: 1 },
      { field: "phone", headerName: "Phone", flex: 0.6 },
      { field: "email", headerName: "Email", flex: 1 },
      {
        field: "actions",
        type: "actions",
        align: "left",
        getActions: ({ row }) => {
          return [
            <Tooltip title="Edit Agency" key={1}>
              <GridActionsCellItem
                label="Edit Agency"
                onClick={() => setOpenAgencyDialog({ mode: "edit", agency: row })}
                icon={<Edit />}
              />
            </Tooltip>,
            <Tooltip title="Delete Agency" key={2}>
              <GridActionsCellItem
                label="Delete Agency"
                onClick={() => setConfirmDeleteLeadAgency(row)}
                icon={<Delete />}
              />
            </Tooltip>,
          ];
        },
      },
    ],
    []
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="nj-lead-agencies"
        isURLDriven
        columns={columns}
        dataQuery={GET_ALL_NJ_LEAD_AGENECIES}
        getItems={(data: any) => data.leadAgencies.items ?? []}
        getCount={(data: any) => data.leadAgencies.count ?? []}
        noDataMessage="No Agencies found."
        initialPageSize={initialPageSize ?? 25}
        onRowClick={({ row }) => {
          setOpenAgencyDialog({ mode: "edit", agency: row });
        }}
        commandButtons={[
          <Button
            key="add-agency-button"
            variant="contained"
            onClick={() => setOpenAgencyDialog({ mode: "add" })}
          >
            Add NJ Lead Agency
          </Button>,
        ]}
      />
      {!!openAgencyDialog && (
        <LeadAgencyDialog
          onClose={() => setOpenAgencyDialog(null)}
          agencyType="NJ Lead Agency"
          mode={openAgencyDialog.mode}
          agencyId={openAgencyDialog?.agency?.id}
        />
      )}

      {confirmDeleteLeadAgency && (
        <ConfirmDeleteNJLeadAgencies
          agencyId={confirmDeleteLeadAgency.id}
          onClose={() => setConfirmDeleteLeadAgency(null)}
        />
      )}
    </Box>
  );
};
